import { keysIn, reverse, sortBy } from "lodash";
import { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";

import { PIE_CHART_COLORS } from "@helpers/charts";
import { assertNonNull } from "@helpers/helpers";
import { pluralize } from "@helpers/string";

import { programTitleTickFormatter } from "./helpers";

const CustomTooltip = ({
  active,
  payload,
  label,
}: TooltipProps<number, string>) => {
  const isVisible = useMemo(
    () => active && payload && payload.length,
    [active, payload]
  );
  const itemContent = useMemo(() => {
    return reverse(sortBy(payload, (item) => item.dataKey)).map((item) => {
      const dataKey = assertNonNull(item.dataKey);
      const responderCount = item.payload.counts[dataKey];
      return (
        <div key={dataKey} style={{ color: item.color }}>
          {item.name}: {item.value}% ({responderCount}{" "}
          {pluralize("responder", responderCount)})
        </div>
      );
    });
  }, [payload]);
  return (
    <div
      className="custom-tooltip"
      style={{ visibility: isVisible ? "visible" : "hidden" }}
    >
      {isVisible && (
        <>
          <div className="font-medium mb-1">{label}</div>
          {itemContent}
        </>
      )}
    </div>
  );
};

const StackedDistributionChart = ({
  data,
  talentIndicatorName,
}: {
  data: any[];
  talentIndicatorName: string;
}) => {
  const answerValues = useMemo(
    () =>
      keysIn(data[0]).filter(
        (key) => !["name", "counts", "labels"].includes(key)
      ),
    [data]
  );

  return (
    <ResponsiveContainer width="75%" height="100%">
      <BarChart data={data} margin={{ right: 90, left: 90, top: 10 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          label={{ value: "Programs", position: "center" }}
          tickFormatter={programTitleTickFormatter}
          interval={0}
          height={90}
          dataKey="name"
        />
        <YAxis
          label={{
            value: talentIndicatorName,
            angle: -90,
            position: "insideLeft",
            dy: 20,
            dx: -20,
          }}
          tickFormatter={(val) => `${Math.round(val)}`}
          domain={[0, 100]}
          allowDataOverflow
          unit="%"
        />
        <Tooltip content={CustomTooltip} />
        <Legend />
        {data.length > 0 &&
          answerValues.sort().map((key, index) => (
            <Bar
              key={key as string}
              dataKey={key as string}
              stackId="a"
              fill={PIE_CHART_COLORS[answerValues.length - 1 - index]}
              name={data[0].labels[key]}
            >
              <LabelList
                fill="#fff"
                formatter={(val: any) => (val > 0 ? `${Math.round(val)}%` : "")}
                dataKey={key as string}
              />
            </Bar>
          ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default StackedDistributionChart;
